import { useEffect, useRef } from 'react'
import toast from 'react-hot-toast'

/**
 * GlobalErrorToast — listens for show:notification events from the API layer
 * and displays toast notifications with optional retry action.
 * Includes deduplication to prevent spam from multiple API calls with same error.
 */
export default function GlobalErrorToast() {
  const recentMessages = useRef(new Map())

  useEffect(() => {
    const handler = (e) => {
      const { type, message, retry } = e.detail || {}
      if (type === 'error' && message) {
        // Deduplicate: skip if same message was shown in the last 5 seconds
        const now = Date.now()
        const lastShown = recentMessages.current.get(message)
        if (lastShown && now - lastShown < 5000) {
          return // Skip duplicate
        }
        recentMessages.current.set(message, now)

        // Clean up old entries (older than 10 seconds)
        for (const [msg, time] of recentMessages.current.entries()) {
          if (now - time > 10000) {
            recentMessages.current.delete(msg)
          }
        }

        if (retry && typeof retry === 'function') {
          toast((t) => (
            <div className="flex items-center gap-3">
              <span className="text-sm">{message}</span>
              <button
                onClick={() => { toast.dismiss(t.id); retry() }}
                className="shrink-0 px-3 py-1 text-xs font-medium bg-primary-600 text-white rounded hover:bg-primary-700"
              >
                Retry
              </button>
            </div>
          ), { duration: 8000, icon: '⚠️' })
        } else {
          toast.error(message, { duration: 5000 })
        }
      }
    }

    window.addEventListener('show:notification', handler)
    return () => window.removeEventListener('show:notification', handler)
  }, [])

  return null
}
